import { Button, Heading, Icon, useToast } from "@elaniintc/goes-design-system";
import ScrollModal from "@/components/common/scroll-modal";
import { TextAreaField } from "@/components/common/text-field";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { formalityReq } from "@/schemas/formality-request";
import { useUpdateFormalityRequestAssignee } from "@/queries/formaly-request";
import type { FormalityRequestType } from "@/types/entities/formality-request";
import SelectField from "@/components/common/select";
import { useEffect, useMemo, useState } from "react";
import type { UserType } from "@/types/entities/users";
import { UserGroupSearchSelector } from "./group-user-search-selector";
import { UserSearchSelector } from "./user-search-selector";
import { UserCard } from "./user-card";

const modalReasonItems = [
  {
    value: "complexity",
    label: "Complejidad de la gestión sobrepasa el nivel del usuario",
  },
  {
    value: "overload",
    label: "El usuario tiene demasiadas gestiones asignadas",
  },
  {
    value: "overdue",
    label: "La gestión ha pasado mucho tiempo sin actualización",
  },
  {
    value: "other",
    label: "Otro motivo (especificar)",
  },
];

interface ModalUserSelectionProps {
  isOpen: boolean;
  selectedRequest?: FormalityRequestType | null;
  groupsImLeaderOf: string[];
  onClose: (user?: UserType) => void;
}

export interface UserGroup {
  id: string;
  name: string;
  last_name: string;
  email: string;
  group_id: string;
  group_name: string;
}

export const RequestAssigneeModal = ({
  isOpen,
  selectedRequest,
  groupsImLeaderOf,
  onClose,
}: ModalUserSelectionProps) => {
  const { showToast } = useToast();
  const { mutateAsync: updateAssignee, isPending: isLoading } =
    useUpdateFormalityRequestAssignee();
  const [selectedNewUser, setSelectedNewUser] = useState<
    UserGroup | UserType | null
  >(null);
  const [userToChange, setUserToChange] = useState<UserGroup | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm({
    resolver: zodResolver(formalityReq),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      reassignedDue: "",
      comment: "",
    },
  });

  const [comment, reassignedDue] = watch(["comment", "reassignedDue"]);
  const canSubmit =
    Boolean(reassignedDue && reassignedDue.length > 0) &&
    Boolean(selectedNewUser) &&
    Boolean(userToChange) &&
    !isLoading &&
    (reassignedDue !== "other" || (comment && comment.trim().length > 0));

  useEffect(() => {
    if (!isOpen) {
      reset();
      setSelectedNewUser(null);
      setError(null);
    }
  }, [isOpen, reset]);

  const onSubmit = async () => {
    if (!selectedRequest?.id || !selectedNewUser?.id) {
      console.error("Missing requestId or selectedNewUser");
      return;
    }

    try {
      setError(null); // Clear any previous errors
      await updateAssignee({
        id: selectedRequest?.id,
        assignments: [
          {
            group_id: (selectedNewUser as UserGroup).group_id,
            assigned_to: selectedNewUser.id,
          },
        ],
        reason_text: comment ?? "",
        reason_id: reassignedDue,
      });
      onClose(selectedNewUser as UserType);
      showToast({
        title: "Reasignación de usuario",
        description: "Usuario reasignado correctamente.",
        options: {
          type: "success",
          duration: 4000,
        },
      });
    } catch (error) {
      console.error("Error updating assignee:", error);

      showToast({
        title: "Reasignación de usuario",
        description:
          "Error al reasignar la gestión. Por favor, inténtalo de nuevo o contacta soporte.",
        options: {
          type: "error",
          duration: 4000,
        },
      });
    }
  };

  const userAssigned = useMemo(() => {
    if (!selectedRequest) return [];

    const userAssigned: UserGroup[] = selectedRequest.groups
      .map((group) => {
        if (groupsImLeaderOf.includes(group.id)) {
          return {
            id: group?.pivot?.assigned_user?.id,
            name: group?.pivot?.assigned_user?.name,
            last_name: group?.pivot?.assigned_user?.last_name || "",
            email: group?.pivot?.assigned_user?.email || "",
            group_id: group.id || "",
            group_name: group.name || "",
          } as UserGroup;
        }

        return null;
      })
      .filter((g) => g !== null);

    if (userAssigned.length === 1) {
      setUserToChange(userAssigned[0] as UserGroup);
    }

    return userAssigned;
  }, [selectedRequest, groupsImLeaderOf]);

  return (
    <ScrollModal
      isOpen={isOpen}
      onClose={() => onClose()}
      modalContentClassName="max-w-[520px] min-h-[680px] w-full gap-0"
      title="Reasignar gestión"
      description="Seleccionar un usuario para reasignar la gestión de la solicitud"
      header={
        <div className="flex items-center justify-between px-5 py-3">
          <Heading variant="h5" className="mb-0!">
            Reasignar gestión
          </Heading>
          <Button
            type="button"
            onClick={() => onClose()}
            variant="quiet"
            aria-label="Cerrar"
            className="w-8 h-8 cursor-pointer"
          >
            <Icon type="far" name="times" />
          </Button>
        </div>
      }
      footer={
        <div className="w-full flex justify-end py-3 px-8 gap-3">
          <Button
            variant="tertiary"
            size="sm"
            type="button"
            onClick={() => onClose()}
            className="cursor-pointer"
          >
            Cancelar
          </Button>
          <Button
            variant="default"
            size="sm"
            type="submit"
            className="cursor-pointer disabled:bg-neutral-object-accent disabled:text-on-disabled disabled:border-none disabled:shadow-none"
            disabled={!canSubmit}
            form="reassign-form"
          >
            {isLoading ? "Reasignando..." : "Reasignar"}
          </Button>
        </div>
      }
    >
      <form
        id="reassign-form"
        className="p-8 flex flex-col gap-8"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-3">
          <UserSearchSelector
            label="Usuario asignado"
            placeholder="Buscar usuario asignado..."
            required
            selectedUser={userToChange ?? undefined}
            userOptions={userAssigned}
            onSelectUser={(data) => {
              setUserToChange({
                id: data.id,
                name: data.name,
                last_name: data.last_name || "",
                email: data.email || "",
                group_id: data.group_id || "",
                group_name: data.group_name || "",
              });
            }}
          />

          {userToChange && (
            <UserCard
              firstName={userToChange.name || ""}
              email={userToChange.email || ""}
              lastName={userToChange?.last_name || ""}
              onClearClick={() => {
                setUserToChange(null);
                setSelectedNewUser(null);
              }}
              disabled={false}
            />
          )}
        </div>

        <div className="flex flex-col gap-3">
          <UserGroupSearchSelector
            label="Reasignar a usuario"
            placeholder="Buscar usuario para reasignación..."
            required
            isDisabled={userToChange === null}
            selectedUser={userToChange ?? undefined}
            formalityId={selectedRequest?.id}
            defaultUsers={userAssigned ?? []}
            groupId={userToChange?.group_id}
            onSelectUser={(data) => {
              setSelectedNewUser({
                id: data.id,
                name: data.name,
                last_name: data.last_name || "",
                email: data.email || "",
                group_id: data.group_id || "",
                group_name: data.group_name || "",
              });
            }}
          />

          {selectedNewUser && (
            <UserCard
              firstName={selectedNewUser.name || ""}
              email={selectedNewUser.email || ""}
              lastName={selectedNewUser?.last_name || ""}
              onClearClick={() => setSelectedNewUser(null)}
              disabled={false}
            />
          )}
        </div>

        <SelectField
          label="¿Por qué reasignas esta gestión?"
          placeholder="Selecciona una opción"
          name="reassignedDue"
          value={reassignedDue}
          required
          options={modalReasonItems}
          onChange={(value) => {
            setValue("reassignedDue", value, { shouldValidate: true });
          }}
          error={errors.reassignedDue?.message || ""}
        />
        <TextAreaField
          label="Especifica tus razones"
          helperText={reassignedDue != "other" ? "Opcional" : ""}
          placeholder="Escribe tu comentario..."
          countAlign="right"
          required={reassignedDue === "other"}
          rows={3}
          limit={120}
          maxLength={120}
          count={comment?.length || 0}
          className="resize-none w-full"
          error={errors.comment?.message || error || ""}
          {...register("comment")}
        />
      </form>
    </ScrollModal>
  );
};
